import type { Location } from "react-router-dom";
import { Page } from "../../../routes/config";

export type ReportTrailItem = {
  label: string;
  to: string;
  state?: unknown;
};

const TRAIL_KEY = "__reportsTrail";

export function readReportTrail(location: Location): ReportTrailItem[] | null {
  const state = (location.state ?? {}) as Record<string, unknown>;
  const trail = state[TRAIL_KEY] as unknown;
  if (!Array.isArray(trail)) return null;
  return trail.filter(
    (t): t is ReportTrailItem =>
      Boolean(t) &&
      typeof (t as any).label === "string" &&
      typeof (t as any).to === "string"
  );
}

export function writeReportTrailState(
  prevState: unknown,
  trail: ReportTrailItem[]
): Record<string, unknown> {
  return { ...(prevState as Record<string, unknown>), [TRAIL_KEY]: trail };
}

export function makeTrailItem(label: string, to: string, state?: unknown): ReportTrailItem {
  return { label, to, state };
}

export function getDefaultTrailForPath(pathname: string, search: string): ReportTrailItem[] {
  const full = `${pathname}${search || ""}`;
  const userFromQuery = new URLSearchParams(search).get("user") ?? "";
  const sku = new URLSearchParams(search).get("sku") ?? "";
  const asn = new URLSearchParams(search).get("asn") ?? "";

  if (pathname.startsWith(Page.REPORTS_INVENTORY_TAG_EPCIS)) {
    return [
      makeTrailItem("Live Inventory", Page.REPORTS_INVENTORY),
      makeTrailItem("Inventory Details", Page.REPORTS_INVENTORY_DETAILS),
      makeTrailItem("Tag EPCIS History", full),
    ];
  }
  if (pathname.startsWith(Page.REPORTS_INVENTORY_DETAILS)) {
    return [
      makeTrailItem("Live Inventory", Page.REPORTS_INVENTORY),
      makeTrailItem(sku ? `Inventory Details (${sku})` : "Inventory Details", full),
    ];
  }
  if (pathname.startsWith(Page.REPORTS_INVENTORY)) {
    return [makeTrailItem("Live Inventory", full)];
  }

  if (pathname.startsWith(Page.REPORTS_RECEIVE_DETAILS)) {
    return [
      makeTrailItem("Receive Report (Tenant)", Page.REPORTS_RECEIVE),
      makeTrailItem(asn ? `Site Receive (${asn})` : "Site Receive Report", full),
    ];
  }
  if (pathname.startsWith(Page.REPORTS_RECEIVE_LINE_ITEMS)) {
    return [
      makeTrailItem("Receive Report (Tenant)", Page.REPORTS_RECEIVE),
      makeTrailItem("Site Receive Report", Page.REPORTS_RECEIVE_DETAILS),
      makeTrailItem(asn ? `Line Items (${asn})` : "Line Items", full),
    ];
  }
  if (pathname.startsWith(Page.REPORTS_RECEIVE_TAG_EPCIS)) {
    return [
      makeTrailItem("Receive Report (Tenant)", Page.REPORTS_RECEIVE),
      makeTrailItem("Site Receive Report", Page.REPORTS_RECEIVE_DETAILS),
      makeTrailItem("Line Items", Page.REPORTS_RECEIVE_LINE_ITEMS),
      makeTrailItem("Tag EPCIS History", full),
    ];
  }
  if (pathname.startsWith(Page.REPORTS_RECEIVE)) {
    return [makeTrailItem("Receive Report (Tenant)", full)];
  }

  if (pathname.startsWith(Page.REPORTS_TRANSFER_DETAILS)) {
    return [
      makeTrailItem("Transfer Report", Page.REPORTS_TRANSFER),
      makeTrailItem(asn ? `Line Items (${asn})` : "Line Items", full),
    ];
  }
  if (pathname.startsWith(Page.REPORTS_TRANSFER)) {
    return [makeTrailItem("Transfer Report", full)];
  }

  if (pathname.startsWith(Page.DI_REPORTS_DETAILS)) {
    return [
      makeTrailItem("Enablement Summary Report", Page.REPORTS_ENABLEMENT),
      makeTrailItem(userFromQuery ? `Enablement Details (${userFromQuery})` : "Enablement Details", full),
    ];
  }
  if (pathname.startsWith(Page.REPORTS_ENABLEMENT)) {
    return [makeTrailItem("Enablement Summary Report", full)];
  }

  return [makeTrailItem("Reports", full)];
}

export function extendTrail(location: Location, next: ReportTrailItem): ReportTrailItem[] {
  const existing = readReportTrail(location);
  const base =
    existing && existing.length > 0
      ? existing
      : getDefaultTrailForPath(location.pathname, location.search);

  // De-dupe if user clicks same row twice etc.
  const trimmed =
    base.length > 0 && base[base.length - 1]?.to === next.to ? base.slice(0, -1) : base;
  return [...trimmed, next];
}

